import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import {
  browserTimeZone,
  isValidTimeZone,
  TIME_ZONE_STORAGE_KEY,
  timeZoneOptions
} from "./timeZone";

interface TimeZonePreference {
  timeZone: string;
  localTimeZone: string;
  options: string[];
  setTimeZone: (value: string) => void;
}

const TimeZoneContext = createContext<TimeZonePreference | null>(null);

export function TimeZoneProvider({ children }: { children: ReactNode }) {
  const localTimeZone = useMemo(browserTimeZone, []);
  const [timeZone, setStoredTimeZone] = useState(() => {
    const stored = window.localStorage.getItem(TIME_ZONE_STORAGE_KEY);
    return stored && isValidTimeZone(stored) ? stored : localTimeZone;
  });
  const options = useMemo(() => timeZoneOptions(localTimeZone), [localTimeZone]);

  const setTimeZone = (value: string) => {
    if (!isValidTimeZone(value)) return;
    window.localStorage.setItem(TIME_ZONE_STORAGE_KEY, value);
    setStoredTimeZone(value);
  };

  return (
    <TimeZoneContext.Provider value={{ timeZone, localTimeZone, options, setTimeZone }}>
      {children}
    </TimeZoneContext.Provider>
  );
}

export function useTimeZone(): TimeZonePreference {
  const preference = useContext(TimeZoneContext);
  if (!preference) throw new Error("useTimeZone must be used inside TimeZoneProvider");
  return preference;
}
